/* eslint-disable @typescript-eslint/no-empty-function */
import { FunctionComponent as F, useState, useEffect } from "react";
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { FilterOptionsState } from "@mui/material/useAutocomplete";
import { IOption } from "@protoplan/types";
import { SxProps, Theme } from "@mui/material";


interface IProps
{
  id?: string,
  label: string,
  currentOption?: IOption | null,
  handleChangeCurrentOption: (option: IOption | null) => void,
  options: IOption[],
  width?: string,
  onInputChange?: (event: React.SyntheticEvent, value: string, reason: string) => void,
  filterOptions?: (options: IOption[], params: FilterOptionsState<IOption>) => IOption[],
  filter?: (options: IOption[], state?: FilterOptionsState<IOption>) => IOption[],
  disabled?: boolean,
  sx?: SxProps<Theme>;
}




const ControlledCreatableAutocomplete: F<IProps> = (props = {
  width: '300px',
  label: '',
  currentOption: null,
  handleChangeCurrentOption: () => {},
  options: [] }) =>
{
  const [options, setOptions] = useState(props.options.map(option => ({ label: option.label, value: option.value })));
  useEffect(() => setOptions(props.options), [props.options]);

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string | IOption | null) =>
  {
    if(typeof newValue === 'string')
      return;

    if(newValue?.input)
    {
      // If user chooses Add "input"
      let newOptions = options.map(option => ({ ...option }));
      const value = options.find(o => o.value < 0) ? Math.min(...options.map(o => o.value)) - 1 : -1;

      newOptions.push({ label: newValue.input, value });
      setOptions(newOptions);
      props.handleChangeCurrentOption({ label: newValue.input, value });
    }
    else
    {
      // If user chooses existing option
      props.handleChangeCurrentOption(newValue);
    }
  };

  const filterOptions = (options: IOption[], params: FilterOptionsState<IOption>) =>
  {
    const filter = props.filter || createFilterOptions<IOption>();
    const filtered = filter(options, params);
    const { inputValue } = params;

    // Suggest the creation of a new value
    const isExisting = options.some(option => inputValue === option.label);
    if (inputValue !== '' && !isExisting)
      filtered.push({ input: inputValue, label: `Add "${inputValue}"`, value: 0 });

    return filtered;
  };

  const getOptionLabel = (option: IOption | string) =>
  {
    // Value selected with enter, right from the input
    if (typeof option === 'string')
      return option;
    // Add "xxx" option created dynamically
    if (option.input)
      return option.input;
    // Regular option
    return option.label;
  };

  return (
    <Autocomplete
      id={props.id}
      size='small'
      value={props.currentOption}
      onChange={handleChange}
      onInputChange={props.onInputChange}
      filterOptions={props.filterOptions || filterOptions}
      autoComplete
      autoSelect
      selectOnFocus
      clearOnBlur={true}
      clearOnEscape={true}
      handleHomeEndKeys
      options={options}
      getOptionLabel={getOptionLabel}
      disabled={props.disabled}
      renderOption={(props, option) => <li {...props}>{option.label}</li>}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      sx={{ ...props.sx, width: props.width }}
      renderInput={params => <TextField {...params} label={props.label}/>}/>);
};


export default ControlledCreatableAutocomplete;
